'use client';

import { HStack } from '@terra/ui-styled-system';
import { memo } from 'react';
import { Edit03Icon, Redo02Icon, Undo02Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { IconButton } from '../../../components/icon-button';
import { Tooltip } from '../../../components/tooltip';

export type UserMessageCheckpointActions = {
  onRestoreCheckpoint: () => void;
  onRedoRestore?: () => void;
  isPending?: boolean;
};

export type UserMessageCardActionsProps = {
  isVisible: boolean;
  onEditUserMessage?: () => void;
  checkpointActions?: UserMessageCheckpointActions;
};

export const UserMessageCardActions = memo(function UserMessageCardActions({
  isVisible,
  onEditUserMessage,
  checkpointActions,
}: UserMessageCardActionsProps) {
  const tBranching = useTranslations('designSystem.chat.branching');
  const tCard = useTranslations('designSystem.chat.userMessageCard');
  const isRedoAvailable = Boolean(checkpointActions?.onRedoRestore);
  const isShown = isVisible || isRedoAvailable;

  return (
    <HStack
      position="absolute"
      top="50%"
      right={1}
      gap={0.5}
      transform="translateY(-50%)"
      opacity={isShown ? 1 : 0}
      pointerEvents={isShown ? 'auto' : 'none'}
      transition="opacity 0.15s ease"
    >
      {checkpointActions?.onRedoRestore ? (
        <Tooltip title={tCard('redoRestore')}>
          <IconButton
            aria-label={tCard('redoRestore')}
            size="xs"
            variant="ghost"
            disabled={checkpointActions.isPending}
            onClick={checkpointActions.onRedoRestore}
          >
            <Redo02Icon size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
      {checkpointActions && !isRedoAvailable ? (
        <Tooltip title={tCard('restoreCheckpoint')}>
          <IconButton
            aria-label={tCard('restoreCheckpoint')}
            size="xs"
            variant="ghost"
            disabled={checkpointActions.isPending}
            onClick={checkpointActions.onRestoreCheckpoint}
          >
            <Undo02Icon size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
      {onEditUserMessage ? (
        <Tooltip title={tBranching('editAndRerun')}>
          <IconButton
            aria-label={tBranching('editAndRerun')}
            size="xs"
            variant="ghost"
            onClick={onEditUserMessage}
          >
            <Edit03Icon size={14} />
          </IconButton>
        </Tooltip>
      ) : null}
    </HStack>
  );
});
